import { readInventorizationContext } from '@/features/inventorization/domain/inventorization-context';
import { readInventorizationIdentity } from '@/features/inventorization/domain/inventorization-identity';
import {
  type InventorizationRegisterReading,
  readInventorizationRegisters,
  readInventorizationRemovedRegisterIds,
} from '@/features/inventorization/domain/inventorization-reading';
import { isDefaultInventorizationRegisterLabel } from '@/features/inventorization/domain/inventorization-registers';
import { readInventorizationTechnicalDetails } from '@/features/inventorization/domain/inventorization-technical';
import { mergeInventorizationRegistersWithExtraction } from '@/features/inventorization/domain/review/inventorization-register-extraction-merge';
import {
  type InventorizationValueInput,
  isEffectiveResolvedInventorizationValue,
  type ResolvedInventorizationValue,
  type ResolvedInventorizationValueStatus,
  resolveInventorizationTextValue,
} from '@/features/inventorization/domain/review/inventorization-resolved-value';
import {
  createInventorizeDetailExtractionPath,
  createInventorizeRegisterExtractionPath,
} from '@/features/inventorization/domain/review/inventorize-extraction-paths';
import {
  INVENTORIZE_CONTEXT_REVIEW_FIELD_CONFIGS,
  INVENTORIZE_IDENTITY_REVIEW_FIELD_CONFIGS,
  INVENTORIZE_LOCATION_TECHNICAL_REVIEW_FIELD_CONFIGS,
  INVENTORIZE_NOTE_REVIEW_FIELD_CONFIG,
  INVENTORIZE_REGISTER_REVIEW_FIELD_CONFIGS,
  INVENTORIZE_REMOTE_READING_REVIEW_FIELD_CONFIGS,
  type InventorizeStaticReviewFieldConfig,
  readInventorizeStaticFieldValue,
} from '@/features/inventorization/domain/review/inventorize-review-field-config';
import {
  createInventorizeRegisterReviewFieldId,
  createInventorizeTechnicalDetailReviewFieldId,
  INVENTORIZE_REGISTER_REVIEW_FIELD_KEYS,
} from '@/features/inventorization/domain/review/inventorize-review-field-ids';
import type { AiExtraction, CaptureSession } from '@/shared/capture/capture-session';
import { getObisOptionsForCommodity } from '@/shared/meters/register-metadata';
import type { PartialInventorizationExtraction } from '@/shared/ocr/inventorize-extraction-contract';
import { getLatestCompletedInventorizeExtraction } from '@/shared/ocr/inventorize-extraction-jobs';

export {
  type InventorizationValueInput,
  isEffectiveResolvedInventorizationValue,
  mergeInventorizationRegistersWithExtraction,
  type ResolvedInventorizationValue,
  type ResolvedInventorizationValueStatus,
  resolveInventorizationTextValue,
};

export function createResolvedInventorizationValueMap(session: CaptureSession) {
  return new Map(createResolvedInventorizationValues(session).map(value => [value.id, value]));
}

export function createResolvedInventorizationValues(session: CaptureSession): ResolvedInventorizationValue[] {
  const context = readInventorizationContext(session);
  const identity = readInventorizationIdentity(session);
  const registers = readResolvedInventorizationRegisters(session);
  const technical = readInventorizationTechnicalDetails(session);
  const extraction = getLatestCompletedInventorizeExtraction(session.aiExtractions);
  const staticFieldInput = {
    aiExtractions: session.aiExtractions,
    context,
    extraction,
    identity,
    technical,
  };

  return [
    ...createResolvedStaticValues(INVENTORIZE_CONTEXT_REVIEW_FIELD_CONFIGS, staticFieldInput),
    ...createResolvedStaticValues(INVENTORIZE_IDENTITY_REVIEW_FIELD_CONFIGS, staticFieldInput),
    ...createResolvedRegisterValues({
      aiExtractions: session.aiExtractions,
      commodity: identity.commodity,
      extraction,
      registers,
    }),
    ...createResolvedStaticValues(INVENTORIZE_LOCATION_TECHNICAL_REVIEW_FIELD_CONFIGS, staticFieldInput),
    ...createResolvedStaticValues(INVENTORIZE_REMOTE_READING_REVIEW_FIELD_CONFIGS, staticFieldInput),
    ...createResolvedDetailValues({
      aiExtractions: session.aiExtractions,
      detail: technical.detail,
      extraction,
    }),
    ...createResolvedStaticValues([INVENTORIZE_NOTE_REVIEW_FIELD_CONFIG], staticFieldInput),
  ];
}

export function readResolvedInventorizationRegisters(session: CaptureSession) {
  const extraction = getLatestCompletedInventorizeExtraction(session.aiExtractions);

  return mergeInventorizationRegistersWithExtraction(
    readInventorizationRegisters(session),
    extraction,
    readInventorizationRemovedRegisterIds(session),
  );
}

function createResolvedStaticValues(
  configs: readonly InventorizeStaticReviewFieldConfig[],
  input: {
    aiExtractions: AiExtraction[];
    context: ReturnType<typeof readInventorizationContext>;
    extraction?: PartialInventorizationExtraction;
    identity: ReturnType<typeof readInventorizationIdentity>;
    technical: ReturnType<typeof readInventorizationTechnicalDetails>;
  },
) {
  return configs.map(config =>
    resolveInventorizationTextValue({
      aiExtractions: input.aiExtractions,
      candidateField: config.candidateField,
      extraction: input.extraction,
      extractionPath: config.extractionPath,
      id: config.id,
      value: readInventorizeStaticFieldValue(config, input),
    }),
  );
}

function createResolvedRegisterValues({
  aiExtractions,
  commodity,
  extraction,
  registers,
}: {
  aiExtractions: AiExtraction[];
  commodity: ReturnType<typeof readInventorizationIdentity>['commodity'];
  extraction?: PartialInventorizationExtraction;
  registers: InventorizationRegisterReading[];
}) {
  return registers.flatMap((register, index) => {
    return INVENTORIZE_REGISTER_REVIEW_FIELD_CONFIGS.map(config =>
      resolveInventorizationTextValue({
        aiExtractions,
        allowedValues:
          config.field === INVENTORIZE_REGISTER_REVIEW_FIELD_KEYS.obis
            ? getObisOptionsForCommodity(commodity).map(option => option.value)
            : undefined,
        candidateField: index === 0 ? config.candidateFieldForFirstRegister : undefined,
        extraction,
        extractionPath: config.extractionKey
          ? createInventorizeRegisterExtractionPath(index, config.extractionKey)
          : undefined,
        id: createInventorizeRegisterReviewFieldId(register.id, config.field),
        source: register.photoRef,
        value: readRegisterValue(register, index, config.field),
      }),
    );
  });
}

function createResolvedDetailValues({
  aiExtractions,
  detail,
  extraction,
}: {
  aiExtractions: AiExtraction[];
  detail: Record<string, string>;
  extraction?: PartialInventorizationExtraction;
}) {
  const extractedDetail =
    extraction?.detail && typeof extraction.detail === 'object' && !Array.isArray(extraction.detail)
      ? extraction.detail
      : {};
  const keys = createResolvedDetailKeys(detail, extractedDetail);

  return [...keys].toSorted().map(key =>
    resolveInventorizationTextValue({
      aiExtractions,
      extraction,
      extractionPath: createInventorizeDetailExtractionPath(key),
      id: createInventorizeTechnicalDetailReviewFieldId(key),
      value: readResolvedDetailValue(detail, key),
    }),
  );
}

function createResolvedDetailKeys(detail: Record<string, string>, extractedDetail: Record<string, unknown>) {
  const keys = new Set([...Object.keys(detail), ...Object.keys(extractedDetail)]);

  if (keys.has('width.value') || keys.has('width.unit')) {
    keys.add('width');
    keys.delete('width.value');
    keys.delete('width.unit');
  }

  return keys;
}

function readResolvedDetailValue(detail: Record<string, string>, key: string) {
  return key === 'width' ? (detail['width.value'] ?? detail.width ?? '') : (detail[key] ?? '');
}

function readRegisterValue(register: InventorizationRegisterReading, index: number, field: string) {
  if (field === 'label') {
    return isDefaultInventorizationRegisterLabel(register.label, index) ? '' : register.label;
  }

  return String(register[field as keyof InventorizationRegisterReading] ?? '');
}
